import {
  resolveClipVariant,
  stripClipPreviewParams,
} from '@src/components/content-components/clipPreviewUrlState';
import { saveBeforeLeaving } from '@src/services/creatorStudio/saveBeforeLeaving';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ContentTab, Studio } from '@src/types.ts/general';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCreatorStudioUnsavedEdits } from './useCreatorStudioUnsavedEdits';

import type { OtherVideo } from '@src/types.ts/story';

/**
 * Compute the Content Studio search params for the "back" navigation out of
 * Creator Studio. Pure so the URL contract is unit-testable without a router.
 *
 * - Creator-only params (`videoId`, `lang`, `newFromOriginal`) are dropped,
 *   mirroring how `useNavigateToCreatorStudio` strips the `clip*` params on
 *   the way in. Everything else (`storyId`, `env`, `showcase`, `tab`, …)
 *   passes through.
 * - When the edited video resolves against the story's clip list,
 *   `clipId=<videoId>` is set so Content Studio reopens the same clip's
 *   preview modal (`resolveClipPreviewSync` handles the rest), and the
 *   transcript language rides back as `clipLang` — the exact inverse of the
 *   `clipLang` -> `lang` mapping the preview's "Edit in Creator Studio" does
 *   on the way in, so the reopened preview shows the language that was being
 *   edited. A video that doesn't resolve — no video loaded, an unsaved
 *   `newFromOriginal` session, or a deleted clip — lands on the plain clip
 *   list, with no language to restore.
 */
export function computeBackToContentStudioSearch(
  current: URLSearchParams,
  currentVideoId: string | null | undefined,
  otherVideos: readonly OtherVideo[] | undefined,
): URLSearchParams {
  const language = current.get('lang');
  const params = stripClipPreviewParams(current);
  params.delete('videoId');
  params.delete('lang');
  params.delete('newFromOriginal');

  if (currentVideoId && resolveClipVariant(otherVideos, currentVideoId)) {
    params.set('clipId', currentVideoId);
    // Only alongside a clip — `clipLang` without `clipId` reopens nothing.
    if (language) params.set('clipLang', language);
  }

  return params;
}

/**
 * The Content Studio search params that open one specific clip's preview — the
 * same URL contract as the Back button, pinned to a given clip and to the tab
 * that can act on it. `SocialClipsView` is the only holder of the `?clipId`
 * sync, so the tab is set explicitly instead of riding along: landing on any
 * other tab opens nothing.
 *
 * Used by Creator Studio's "Open Clip" toast action (PLAT-11752) — the panel
 * unmounts on navigation, so the clip is handed over as a deep link rather
 * than opened directly.
 */
export function computeOpenClipInContentStudioSearch(
  current: URLSearchParams,
  clipId: string,
  otherVideos: readonly OtherVideo[] | undefined,
): URLSearchParams {
  const params = computeBackToContentStudioSearch(current, clipId, otherVideos);
  params.set('tab', ContentTab.video_clip);
  return params;
}

const logger = LogManager.getLogger('hook:useNavigateBackToContentStudio');

export interface NavigateBackToContentStudio {
  navigateBack: () => void;
  /** Saving the video's last edits before leaving (PLAT-11929). */
  saving: boolean;
}

/**
 * Navigate from Creator Studio back to Content Studio — always an explicit,
 * computed deep link (never `history.back()`), so it behaves identically for
 * users who came from Content Studio and users who entered Creator Studio
 * directly (DatoCMS "Open Editor" link, bookmark).
 *
 * PLAT-11929 — edits not yet saved are saved first, and the navigation waits
 * for that save: the clip preview it opens reads the last saved version, so
 * leaving straight away dropped them. Nothing unsaved means no save and no
 * wait. A failed save stays here, where the store's retry prompt is shown.
 */
export function useNavigateBackToContentStudio(): NavigateBackToContentStudio {
  const videoCreator = useVideoCreatorStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasUnsavedEdits = useCreatorStudioUnsavedEdits(videoCreator);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const navigateBack = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const result = await saveBeforeLeaving(videoCreator, hasUnsavedEdits);
    savingRef.current = false;
    if (!mountedRef.current) return;
    setSaving(false);

    if (!result.ok) {
      logger.warn('Stayed in Creator Studio: the save before leaving failed', {
        videoId: videoCreator.currentVideo?.id,
        error: result.error,
      });
      return;
    }

    // Read after the save: a first save can swap a temporary id for the real one.
    const currentVideoId =
      videoCreator.currentVideo?.id ?? searchParams.get('videoId');
    navigate({
      pathname: `/${Studio.content}`,
      search: computeBackToContentStudioSearch(
        searchParams,
        currentVideoId,
        videoCreator.story?.otherVideos,
      ).toString(),
    });
  }, [hasUnsavedEdits, navigate, searchParams, videoCreator]);

  return {
    navigateBack: () => void navigateBack(),
    saving,
  };
}
